import { create } from "zustand";
import { ExpertInfo, CounselingCategory, OfficeInfo as ApiOfficeInfo } from "@src/api/expert/expertInfo";

export type OfficeInfo = ApiOfficeInfo;

export type ExpertInfoItem = {
    phone: string;
    officeInfo?: OfficeInfo;
    counselingCategories: CounselingCategory[];
    career: string; // API 전송용 JSON 문자열
    careerObj: Partial<Record<CounselingCategory, number>>; // 화면용 객체
    qualification?: string;
    introduction?: string;
    auctionAgentCertFileUrl?: string;
    updatedAt: string;
    type: string;
    file: { url: string };
    bankName?: string;
    bankAccount?: string;
};

export type ExpertInfoState = {
    form: {
        infos: ExpertInfoItem[];
        no: number;
        profile: string;
        name: string;
    };
    validationErrors: Record<string, string>;
    isEditing: boolean;

    setPartialState: (
        partial: Partial<ExpertInfoState["form"]> | ((prev: ExpertInfoState["form"]) => Partial<ExpertInfoState["form"]>),
    ) => void;

    setProfileUrl: (url: string | null) => void;
    reset: () => void;
};

// 초기값 정의
const initialForm: ExpertInfoState["form"] = {
    infos: [
        {
            phone: "",
            officeInfo: undefined,
            counselingCategories: [],
            career: "{}", // API용
            careerObj: {}, // 화면용
            qualification: "",
            introduction: "",
            auctionAgentCertFileUrl: "",
            updatedAt: new Date().toISOString(),
            type: "",
            file: { url: "" },
            bankName: "",
            bankAccount: "",
        },
    ],
    no: 0,
    profile: "",
    name: "",
};

export const useExpertInfoStore = create<ExpertInfoState>((set) => ({
    form: initialForm,
    validationErrors: {},
    isEditing: false,

    // nested 객체까지 안전하게 병합
    setPartialState: (partial) =>
        set((state) => {
            if (typeof partial === "function") {
                const result = partial(state.form);
                return {
                    form: {
                        ...state.form,
                        ...result,
                        infos: result.infos
                            ? result.infos.map((item, idx) => ({
                                  ...state.form.infos[idx],
                                  ...item,
                                  officeInfo: item.officeInfo
                                      ? {
                                            ...state.form.infos[idx]?.officeInfo,
                                            ...item.officeInfo,
                                        }
                                      : state.form.infos[idx]?.officeInfo,
                              }))
                            : state.form.infos,
                    },
                };
            }
            return {
                form: {
                    ...state.form,
                    ...partial,
                    infos: partial.infos
                        ? partial.infos.map((item, idx) => ({
                              ...state.form.infos[idx],
                              ...item,
                              officeInfo: item.officeInfo
                                  ? {
                                        ...state.form.infos[idx]?.officeInfo,
                                        ...item.officeInfo,
                                    }
                                  : state.form.infos[idx]?.officeInfo,
                          }))
                        : state.form.infos,
                },
            };
        }),

    setProfileUrl: (url) =>
        set((state) => ({
            form: { ...state.form, profile: url ?? "" },
        })),

    reset: () =>
        set({
            form: { ...initialForm, infos: initialForm.infos.map((i) => ({ ...i })) },
            validationErrors: {},
            isEditing: false,
        }),
}));

type ProfileURL = {
    profileImage: string;
};

type ProfileState = {
    psrofileImage: ProfileURL;
    setProfileImage: (info: Partial<ProfileURL>) => void;
    reset: () => void;
};

export const useProfileStore = create<ProfileState>((set, get) => ({
    psrofileImage: { profileImage: "" },

    setProfileImage: (info) =>
        set({
            psrofileImage: {
                ...get().psrofileImage,
                ...info,
            },
        }),

    reset: () =>
        set({
            psrofileImage: { profileImage: "" },
        }),
}));

type CertificateURL = {
    certificateImage: string;
};

type CertificateState = {
    certificateImage: CertificateURL;
    setCertificateImage: (info: Partial<CertificateURL>) => void;
    reset: () => void;
};

export const useCertificateStore = create<CertificateState>((set, get) => ({
    certificateImage: { certificateImage: "" },

    setCertificateImage: (info) =>
        set({
            certificateImage: {
                ...get().certificateImage,
                ...info,
            },
        }),

    reset: () =>
        set({
            certificateImage: { certificateImage: "" },
        }),
}));
